import {
  ChangeEvent,
  ForwardedRef,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { AsYouType, getCountryCallingCode } from 'libphonenumber-js'
import { useTranslations } from '@/core/translation'
import Input, { inputClasses } from '../Input'
import PhoneNumberCountryDropdown from './PhoneNumberCountryDropdown'
import { PhoneNumberCountryProps } from './PhoneNumberCountryDropdown'
import { PhoneNumberInputProps } from './PhoneNumberInput.types'
import { phoneNumberStyles as styles } from './PhoneNumberInput.styles'
import { DEFAULT_PHONE_COUNTRY } from './PhoneNumberInput.data'
import { findCountryOption } from './PhoneNumberInput.utils'

const PhoneNumberInput = forwardRef(
  (
    {
      defaultCountry = DEFAULT_PHONE_COUNTRY,
      country,
      value,
      onChange,
      onCountyChange,
      ...props
    }: PhoneNumberInputProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const t = useTranslations('components')

    const inputRef = useRef<HTMLInputElement | null>(null)

    const [selectedOption, setSelectedOption] = useState<PhoneNumberCountryProps>(
      findCountryOption(defaultCountry),
    )

    const displayValue = useMemo(() => {
      const str = typeof value === 'string' ? value : value != null ? String(value) : ''
      return new AsYouType(selectedOption.value).input(str)
    }, [value, selectedOption.value])

    const handleChange: NonNullable<PhoneNumberInputProps['onChange']> = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target?.value ?? ''
        const formatter = new AsYouType(selectedOption.value)
        let formattedValue = formatter.input(inputValue)

        const isDeleting = inputValue.length < displayValue.length
        const formatterBlockedDeletion = formattedValue.length >= displayValue.length

        if (isDeleting && formatterBlockedDeletion) {
          const digits = inputValue.replace(/\D/g, '')
          formattedValue = new AsYouType(selectedOption.value).input(digits.slice(0, -1))
        }

        event.target.value = formattedValue
        onChange?.(event)
      },
      [onChange, selectedOption.value, displayValue],
    )

    const handleSelectCountry = useCallback(
      (option: PhoneNumberCountryProps) => {
        setSelectedOption(option)
        onCountyChange?.(option.value)
      },
      [onCountyChange],
    )

    useEffect(() => {
      setSelectedOption(findCountryOption(defaultCountry))
    }, [defaultCountry])

    useEffect(() => {
      if (country) setSelectedOption(findCountryOption(country))
    }, [country])

    return (
      <Input
        numeric
        ref={ref}
        inputWrapperProps={{ ref: inputRef }}
        value={displayValue}
        label={t('phoneNumberInput.label')}
        labelProps={{
          sx: {
            [`.${inputClasses.label.placement.inside}.${inputClasses.state.startAdornment} &`]: {
              transform: `translate(${30 + getCountryCallingCode(selectedOption.value).length * 10}px, 10px)`,
            },
          },
        }}
        startAdornment={
          <PhoneNumberCountryDropdown
            inputRef={inputRef}
            selectedOption={selectedOption}
            setSelectedOption={handleSelectCountry}
          />
        }
        startAdornmentProps={{
          sx: styles.startAdornment,
          onClick: (event) => {
            event.stopPropagation()
          },
        }}
        onChange={handleChange}
        {...props}
      />
    )
  },
)

PhoneNumberInput.displayName = 'PhoneNumberInput'

export default memo(PhoneNumberInput)
